import { useEffect, useState } from "react";
import { listSatinJobs } from "./satinJobStorage";
import { parseSatinJobContext, SATIN_JOB_EVENT } from "./satinJobEvents";
import "./SatinJobQueue.css";

const elapsedLabel = (startedAt, now) => {
  const seconds = Math.max(0, Math.floor((now - (startedAt || now)) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

export default function SatinJobQueue({ fileId, onSelectLayer }) {
  const [jobs, setJobs] = useState({});
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let active = true;
    Promise.resolve(listSatinJobs() || [])
      .then((saved) => {
        if (!active) return;
        const pending = {};
        saved.forEach(({ context, value }) => {
          const parsed = parseSatinJobContext(context);
          if (
            parsed &&
            parsed.fileId === (fileId || "unsaved") &&
            !value?.consumed &&
            Date.now() - (value?.startedAt || 0) < 30 * 60 * 1000
          ) {
            pending[context] = {
              context,
              layerIndex: parsed.layerIndex,
              startedAt: value.startedAt,
              message: "Waiting for Trace…",
              phase: "working",
            };
          }
        });
        setJobs(pending);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [fileId]);

  useEffect(() => {
    const update = ({ detail }) => {
      const parsed = parseSatinJobContext(detail?.context);
      if (!parsed || parsed.fileId !== (fileId || "unsaved")) return;
      setJobs((current) => {
        const next = { ...current };
        if (detail.phase === "working") {
          next[detail.context] = { ...detail, layerIndex: parsed.layerIndex };
        } else {
          delete next[detail.context];
        }
        return next;
      });
    };
    window.addEventListener(SATIN_JOB_EVENT, update);
    return () => window.removeEventListener(SATIN_JOB_EVENT, update);
  }, [fileId]);

  useEffect(() => {
    if (!Object.keys(jobs).length) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [jobs]);

  const activeJobs = Object.values(jobs).sort((a, b) => a.startedAt - b.startedAt);
  if (!activeJobs.length) return null;

  return (
    <aside className="satin-job-queue" aria-label="Trace processing queue">
      <div className="satin-job-queue-heading">
        <span>Trace is working</span>
        <small>{activeJobs.length} {activeJobs.length === 1 ? "layer" : "layers"}</small>
      </div>
      {activeJobs.map((job) => (
        <button
          type="button"
          className="satin-job-queue-item"
          key={job.context}
          onClick={() => onSelectLayer?.(job.layerIndex)}
        >
          <span className="satin-job-queue-spinner" aria-hidden="true" />
          <span className="satin-job-queue-copy">
            <strong>Layer {job.layerIndex + 1}</strong>
            <small>{job.message || "Creating satin columns…"}</small>
          </span>
          <span className="satin-job-queue-time">{elapsedLabel(job.startedAt, now)}</span>
          <span className="satin-job-queue-arrow" aria-hidden="true">→</span>
        </button>
      ))}
    </aside>
  );
}
